import React, { useEffect, useState } from "react";
import { Card, Table, Typography, Input, Space, Tooltip, Button, Badge, DatePicker, Select, Row, Col, Form } from "antd";
import { SearchOutlined, PlusOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { ProjectService } from "../../../Services/Manage/ProjectService";
import { UserService } from "../../../Services/Manage/UserService";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

const { RangePicker } = DatePicker;

const { Title } = Typography;

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find((p) => p.link === link) || { add: false, edit: false, delete: false, read: false };
    } catch {
        return { add: false, edit: false, delete: false, read: false };
    }
};

const OverviewProject = () => {
    const perm = getPermission("/overview-project");

    const viewAll = perm.view_all === true;

    const navigate = useNavigate();

    const [projects, setProjects] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [dateRange, setDateRange] = useState([]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);

    const getAllProjects = async () => {
        try {
            const request = await ProjectService.getAllProject();
            let rows = request.data || [];

            if (!viewAll) {
                const user = await UserService.getOmeUser();
                const uid = String(user.uid).trim();

                rows = rows.filter((p) => {
                    let engineers = p.pro_engineer;
                    if (typeof engineers === "string") {
                        try { engineers = JSON.parse(engineers); } catch { engineers = []; }
                    }
                    const isEngineer = (engineers || []).some(
                        (id) => String(id).trim() === uid
                    );
                    return (
                        String(p.pro_manager ?? "").trim() === uid ||
                        String(p.added_by ?? "").trim() === uid ||
                        isEngineer
                    );
                });
            }

       
            setProjects(rows);
        } catch (error) {
            console.error(error);
        }
    };
    const [userMap, setUserMap] = useState({});

    const loadUserMap = async () => {
        try {
            const res = await UserService.getAllUsers();
            const map = {};
            (res.data || []).forEach((u) => {
                if (u.user_id) map[String(u.user_id)] = u.name;
            });
            setUserMap(map);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        getAllProjects();
        loadUserMap();
    }, []);

    const departmentOptions = [...new Set(projects.map((p) => p.department_name).filter(Boolean))].map((d) => ({
        label: d,
        value: d,
    }));

    const filteredProjects = projects.filter((p) => {
        const text = searchText.toLowerCase();
        const matchesSearch =
            p.pro_name?.toLowerCase().includes(text) ||
            p.pro_no?.toLowerCase().includes(text) ||
            p.pro_customer?.toLowerCase().includes(text);

        let matchesDate = true;
        if (dateRange && dateRange.length === 2) {
            const [start, end] = dateRange;
            let projectDate = dayjs(p.pro_start_date, "DD-MM-YYYY", true);
            if (!projectDate.isValid()) projectDate = dayjs(p.pro_start_date);
            matchesDate = projectDate.isValid() && projectDate.isBetween(start, end, "day", "[]");
        }

        const matchesDept =
            selectedDepartments.length === 0 || selectedDepartments.includes(p.department_name);

        return matchesSearch && matchesDate && matchesDept;
    });

    const columns = [
        { title: "S.No", key: "sno", align: "center", width: 60, render: (_, __, i) => i + 1 },
        {
            title: "Project Name",
            dataIndex: "pro_name",
            width: 200,
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{text || "-"}</div>
                    <div style={{ fontSize: 12, color: "#888" }}>{record.pro_customer || "-"}</div>
                </div>
            ),
        },
        { title: "Project No.", dataIndex: "pro_no", width: 110, render: (t) => <strong>{t || "-"}</strong> },
        { title: "Department", dataIndex: "department_name", width: 130, render: (t) => t || "-" },
        { title: "Project Manager", dataIndex: "pro_manager", width: 140, render: (t) => userMap[String(t)] || t || "-" },
        {
            title: "Engineer",
            dataIndex: "pro_engineer",
            width: 140,
            render: (v) => {
                let engineers = v;
                if (typeof engineers === "string") {
                    try { engineers = JSON.parse(engineers); } catch { engineers = []; }
                }
                if (!Array.isArray(engineers) || engineers.length === 0) return "-";

                const names = engineers.map((id) => userMap[String(id)] || id);

                if (names.length <= 2) return names.join(", ");

                return (
                    <Tooltip title={names.join(", ")}>
                        <span>{names.slice(0, 2).join(", ")}...</span>
                    </Tooltip>
                );
            },
        },

        { title: "Team Size", dataIndex: "pro_team_size", align: "center", width: 100, render: (v) => v ?? "-" },
        { title: "Start Date", dataIndex: "pro_start_date", width: 110, render: (v) => v || "-" },
        { title: "Target End Date", dataIndex: "pro_target_end_date", width: 130, render: (v) => v || "-" },
        {
            title: "Added On",
            dataIndex: "added_on",
            width: 110,
            render: (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "-"),
        },
        {
            title: "Status",
            dataIndex: "pro_status",
            align: "center",
            width: 110,
            render: (v) => <Badge status="processing" text={v || "-"} />,
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: 220,
            render: (_, record) => (
                <Space>
                    <Tooltip title={!perm.read ? "You don't have permission to view" : ""}>
                        <Button
                            type="primary"
                            size="small"
                            icon={<EyeOutlined />}
                            disabled={!perm.read}
                            onClick={() =>
                                navigate("/overview-project/view", {
                                    state: { id: record.id },
                                })
                            }
                        >
                            View
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card
    title={<Title level={4} style={{ margin: 0, color: "#1658b4" }}>Overview Project</Title>}
        >
            <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item label="Search" layout="vertical" style={{ marginBottom: 0 }}>
                            <Input
                                placeholder="Search Project Name, No., Customer..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item label="Select Date" layout="vertical" style={{ marginBottom: 0 }}>
                            <RangePicker
                                allowClear
                                style={{ width: "100%" }}
                                onChange={(dates) => setDateRange(dates)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item label="Select Departments" layout="vertical" style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                mode="multiple"
                                placeholder="Select departments"
                                options={departmentOptions}
                                value={selectedDepartments}
                                onChange={setSelectedDepartments}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>

            <Table
                rowKey="id"
                dataSource={filteredProjects}
                columns={columns}
                pagination={{ pageSize: 10 }}
                size="small"
                scroll={{ x: "max-content" }}
            />
        </Card>
    );
};

export default OverviewProject;